import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircleIcon,
  LocationIcon,
  PhoneIcon,
  SavedLocationIcon,
  TrashIcon,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { stagedPlaceLabels } from '@hero/agent';
import type { PublishOrderLocationDraft } from '@hero/hooks-shared';
import { Button } from '../../../../ui/Button/Button';

export interface LocationPickerRowProps {
  tone: 'origin' | 'dest';
  label: string;
  location: PublishOrderLocationDraft | null;
  onOpen: () => void;
  onClear?: () => void;
  /**
   * Optional footer rendered inside the same bordered card, separated by a
   * subtle divider. Used by destination rows in the order publish flow to host
   * the trucks counter so it visually belongs to the destination.
   */
  footer?: React.ReactNode;
}

/**
 * Visual row used by anything that picks a Hero-side location (publish-order
 * RouteSection, Route form, future agreement lane fields…). Stateless on
 * purpose; the host wires `onOpen` to whatever picker drawer it owns.
 *
 * The whole card body is interactive: clicking anywhere on a *filled* row
 * opens the picker (so the user can edit/replace the pick) and clicking
 * anywhere on an *empty* row also opens it. The trash icon and the
 * "Edit"/"Pick" button stay opt-out so the user can hit them without
 * tripping the body click.
 */

export function LocationPickerRow({
  tone,
  label,
  location,
  onOpen,
  onClear,
  footer,
}: LocationPickerRowProps) {
  const { t } = useTranslation(['m-order', 'c-main']);
  const isApproximate = location?.isAccurate === false;
  const hasCoords = Boolean(location && location.lat !== 0 && location.lng !== 0);
  const hasNameHint = Boolean(location?.name?.trim());
  const hasLocation = hasCoords || hasNameHint;
  const contactName = location?.contactName?.trim();
  const contactPhone = location?.contactPhone?.trim();
  const hasContact = Boolean(contactName || contactPhone);
  // The city names the stop and the place qualifies it — a warehouse
  // label alone leaves the reader guessing which city they are looking
  // at, and it hides a pin drag that crossed into another one.
  const isSaved = Boolean(location?.savedLocationId);
  const { city: primaryTitle = '', place: placeName } = stagedPlaceLabels(
    location ?? undefined,
  );

  const bodyInteractiveProps = {
    role: 'button' as const,
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
    'aria-label':
      (hasLocation
        ? t('wizard.location.edit', { defaultValue: 'Edit' })
        : t('wizard.location.pick', { defaultValue: 'Pick' })) +
      ' — ' +
      label,
  };

  return (
    <div
      className={cn(
        'rounded-lg border transition-colors overflow-hidden',
        hasLocation
          ? isApproximate
            ? 'border-amber-300 bg-amber-50'
            : 'border-primary/30 bg-primary/5'
          : 'border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer focus-within:ring-2 focus-within:ring-primary/30',
      )}>
      <div
        className="flex items-start gap-3 px-3 py-2.5 outline-none cursor-pointer"
        {...bodyInteractiveProps}>
        <div
          className={cn(
            'mt-0.5 flex size-7 items-center justify-center rounded-full text-white flex-shrink-0',
            !hasLocation ? 'bg-gray-300' : tone === 'origin' ? 'bg-amber-500' : 'bg-sky-500',
          )}>
          <LocationIcon className="size-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
          {hasLocation && location ? (
            <>
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="text-sm font-semibold truncate">{primaryTitle}</span>
                {placeName && (
                  <span
                    title={placeName}
                    className={cn(
                      'flex min-w-0 shrink items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                      isSaved
                        ? 'bg-primary/10 text-primary-700'
                        : 'bg-slate-100 text-slate-600',
                    )}>
                    {isSaved && <SavedLocationIcon className="size-2.5 shrink-0" />}
                    <span className="truncate">{placeName}</span>
                  </span>
                )}
              </div>
              {!hasCoords && hasNameHint ? (
                <div className="text-xs text-amber-600 truncate">
                  {t('wizard.location_picker.accuracy.approximate_badge', {
                    defaultValue: 'Approximate — tap to confirm',
                  })}
                </div>
              ) : null}
              {location.fullAddress && (
                <div className="text-xs text-gray-500 truncate">{location.fullAddress}</div>
              )}
              {hasContact && (
                <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-600 truncate">
                  <PhoneIcon className="size-3 text-gray-400 flex-shrink-0" />
                  <span className="truncate">
                    {[contactName, contactPhone].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm italic text-gray-400">
              {t('wizard.location.empty', { defaultValue: 'Not chosen yet' })}
            </div>
          )}
        </div>
        <div
          className="flex items-center gap-1 flex-shrink-0"
          // Stop clicks on the action group from bubbling up to the
          // body click (which would re-open the picker behind the
          // user's intent — e.g. clicking the trash icon).
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}>
          {hasLocation && location && isApproximate && (
            <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1 bg-amber-100 text-amber-700">
              <AlertCircleIcon className="size-3" />
              {t('wizard.location_picker.accuracy.approximate_badge', {
                defaultValue: 'Approximate',
              })}
            </span>
          )}
          <Button size="sm" variant="ghost" onClick={onOpen}>
            {hasLocation
              ? t('wizard.location.edit', { defaultValue: 'Edit' })
              : t('wizard.location.pick', { defaultValue: 'Pick' })}
          </Button>
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              aria-label={t('c-main:clear', { defaultValue: 'Clear' })}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
              <TrashIcon className="size-4" />
            </button>
          )}
        </div>
      </div>
      {footer && (
        <div
          className={cn(
            'border-t px-3 py-2',
            isApproximate
              ? 'border-amber-200/70 bg-amber-50/60'
              : 'border-primary/15 bg-white/40',
          )}>
          {footer}
        </div>
      )}
    </div>
  );
}
